// src/components/StatutAo.jsx — LA LECTURE AO D'UN SCORE (serveur, s28-s29).
//
// Expert AO, 30.10 : « le plafond n'est pas une élimination ». À côté du score
// brut : « Éliminé AO » (la fiche dit les indispensables EXCLUANTES) ou
// « Bloc perdu » (grille en tout ou rien), le score EN L'ÉTAT et le score
// POTENTIEL. Puis : « une réponse NON du candidat doit faire tomber le
// potentiel » — chaque point « À confirmer » se marque confirmé, infirmé ou
// en attente (PUT /besoins/:id/confirmations-ao/:cid), et le serveur renvoie
// la lecture relue. L'écran ne recalcule rien. Sans statut : rien.
import React, { useEffect, useRef, useState } from 'react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';
const REPONSES = [
  { cle: 'confirme', libelle: 'Confirmé', icone: 'check-circle' },
  { cle: 'infirme', libelle: 'Infirmé', icone: 'x-circle' },
  { cle: 'en_attente', libelle: 'En attente', icone: 'hourglass-split' },
];

export default function StatutAo({ statut, libelle, enLEtat, potentiel, aConfirmer = [], confirmations = null,
  besoinId = null, candidateId = null, large = false, showNotification }) {
  const [lu, setLu] = useState(null);          // la lecture renvoyée après une réponse
  const [ouvert, setOuvert] = useState(false);  // la liste, en compact
  const [busy, setBusy] = useState(null);
  const ref = useRef(null);
  useEffect(() => { setLu(null); }, [statut, enLEtat, potentiel]);
  useEffect(() => {
    if (!ouvert) return undefined;
    const fermer = (e) => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    const echap = (e) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', fermer);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', fermer); document.removeEventListener('keydown', echap); };
  }, [ouvert]);

  const st = lu ? lu.statut_ao : statut;
  if (!st) return null;
  const elimine = st === 'elimine';
  const etat = lu ? lu.score_en_l_etat : enLEtat;
  const pot = lu ? lu.score_potentiel : potentiel;
  const points = (lu ? lu.confirmations_ao : confirmations)
    || (aConfirmer || []).map(c => ({ critere: c, cite: null, statut: 'en_attente' }));
  const editable = besoinId != null && candidateId != null;

  const repondre = async (critere, reponse) => {
    setBusy(`${critere}|${reponse}`);
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/confirmations-ao/${candidateId}`, {
        method: 'PUT', headers: { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ critere, statut: reponse }),
      });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && (!j.error || /route non trouv/i.test(j.error))) throw new Error('Les réponses du candidat arrivent côté serveur.');
      if (!r.ok || j.success === false) throw new Error(j.error || `Refusé (${r.status}).`);
      if (j.lecture) setLu(j.lecture);
      showNotification?.(j.message, 'success');
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const titre = [lu ? lu.statut_ao_libelle : libelle, points.length ? `À confirmer avec le candidat : ${points.map(p => p.critere).join(', ')}.` : null,
    pot != null ? `Potentiel ${pot} si elles sont confirmées.` : null].filter(Boolean).join(' ');

  const liste = (
    <div style={{ marginTop: '6px' }}>
      <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--erp-ink)' }}>À confirmer avec le candidat</div>
      {points.map(p => (
        <div key={p.critere} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', padding: '5px 0', borderTop: '1px solid var(--erp-line)' }}>
          <span style={{ flex: 1, minWidth: '140px', fontSize: '12px', color: 'var(--erp-ink)' }}>
            {p.critere}
            {p.cite != null && (
              <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--erp-text-tertiary)' }}>
                {p.cite ? 'cité dans le CV, sans mission qui le montre' : 'absent du CV — ne compte au potentiel que confirmé'}
              </span>
            )}
          </span>
          {editable ? REPONSES.map(x => (
            <button key={x.cle} type="button" disabled={!!busy} onClick={() => repondre(p.critere, x.cle)}
              aria-pressed={p.statut === x.cle}
              className={`erp-btn erp-btn--sm ${p.statut === x.cle ? 'erp-btn--primary' : 'erp-btn--secondary'}`}>
              <i className={`bi bi-${busy === `${p.critere}|${x.cle}` ? 'arrow-repeat' : x.icone}`} aria-hidden="true" /> {x.libelle}
            </button>
          )) : <span style={{ fontSize: '11px', color: 'var(--erp-text-tertiary)' }}>{REPONSES.find(x => x.cle === p.statut)?.libelle}</span>}
        </div>
      ))}
    </div>
  );

  const pastille = (
    <span className="bd-mr-chip" title={titre} role={!large && editable ? 'button' : undefined} tabIndex={!large && editable ? 0 : undefined}
      onClick={!large && editable ? (e) => { e.stopPropagation(); setOuvert(o => !o); } : undefined}
      onKeyDown={!large && editable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOuvert(o => !o); } } : undefined}
      style={{ background: elimine ? 'var(--erp-danger-soft)' : 'var(--erp-warning-soft)', color: elimine ? 'var(--erp-danger)' : 'var(--erp-warning)',
        whiteSpace: 'nowrap', cursor: !large && editable ? 'pointer' : undefined }}>
      <i className={`bi bi-${elimine ? 'x-octagon' : 'exclamation-triangle'}`} aria-hidden="true" />{' '}
      {elimine ? 'Éliminé AO' : 'Bloc indispensables perdu'}
      {etat != null && pot != null && (
        <span className="ats-mono" style={{ fontWeight: 400 }}> · en l’état {etat} · potentiel {pot}</span>
      )}
    </span>
  );

  if (large) {
    return (
      <span style={{ display: 'block' }} onClick={e => e.stopPropagation()}>
        {pastille}
        {points.length > 0 && liste}
      </span>
    );
  }
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      {pastille}
      {ouvert && points.length > 0 && (
        <span className="erp-card" onClick={e => e.stopPropagation()} role="dialog" aria-label="À confirmer avec le candidat"
          style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50, width: 'min(420px, 90vw)', padding: '10px 12px', display: 'block', textAlign: 'left' }}>
          {liste}
        </span>
      )}
    </span>
  );
}
